<template>
  <div class="w-full relative bg-white">

  <!-- Resume upload modal — shown when a Guide selects PASSED for the resume stage -->
  <ResumeUploadModal
    v-if="resumeUploadModal.show && resumeUploadModal.adventurer"
    :user-id="resumeUploadModal.adventurer.profile_details.user_details.id"
    :adventurer-name="`${resumeUploadModal.adventurer.profile_details.user_details.first_name} ${resumeUploadModal.adventurer.profile_details.user_details.last_name}`"
    @success="onResumeUploadSuccess"
    @cancel="onResumeUploadCancel"
  />
    <table class="w-full border-collapse min-w-[900px] border border-gray-200 rounded-md">
      <thead>
        <tr class="border-b border-gray-200 bg-gray-50">
          <th
            class="text-left py-4 px-6 font-medium text-xs uppercase tracking-wider text-gray-500 min-w-[280px] w-[350px]"
          >
            <button
              @click="toggleNameSort"
              class="flex items-center gap-1 hover:text-gray-700 transition-colors group"
            >
              Adventurer
              <span class="text-gray-400 group-hover:text-gray-600">
                <ArrowUpDown v-if="!nameSortDirection" class="w-3 h-3" />
                <ArrowUp v-else-if="nameSortDirection === 'asc'" class="w-3 h-3" />
                <ArrowDown v-else class="w-3 h-3" />
              </span>
            </button>
          </th>
          <th
            v-for="stage in stages"
            :key="stage"
            class="text-left py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500 min-w-[130px]"
          >
            {{ getStageTitle(stage) }}
          </th>
          <th
            class="text-center py-4 px-4 font-medium text-xs uppercase tracking-wider text-gray-500 w-16 min-w-[64px]"
          >
            <span class="sr-only">Expand</span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100">
        <template v-for="adventurer in sortedAdventurers" :key="adventurer.id">
          <!-- Main row -->
          <tr 
            class="group hover:bg-gray-50/50 transition-colors cursor-pointer"
            :class="{ 'bg-gray-50/50': expandedRowId === adventurer.id }"
            @click="toggleRowExpansion(adventurer.id)"
          >
            <!-- Adventurer Info Column -->
            <td class="py-4 px-6 min-w-[280px] w-[350px]">
              <div class="flex items-center gap-4">
                <FellowAvatarDropdown
                  :user-id="adventurer.profile_details.user_details.id"
                  :username="adventurer.profile_details.user_details.username"
                  :avatar-src="adventurer.profile_details?.user_details?.avatar_image"
                  :name="`${adventurer.profile_details?.user_details?.first_name} ${adventurer.profile_details?.user_details?.last_name}`"
                  :curriculum-path="`/manage/campus/fellows/${adventurer.profile_details.user_details.id}`"
                  :is-blurred="isBlurred"
                  @click.stop
                />

                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-medium text-gray-900 truncate flex items-center gap-2" :class="{ 'blur-md select-none': isBlurred }">
                    {{ adventurer.profile_details?.user_details?.first_name }}
                    {{ adventurer.profile_details?.user_details?.last_name }}
                  </h3>
                  <div class="flex items-center gap-3 mt-0.5">
                    <p class="text-xs text-gray-500 truncate max-w-[140px]" :class="{ 'blur-md select-none': isBlurred }">
                      {{ adventurer.profile_details?.user_details?.email }}
                    </p>
                    <div v-if="!isBlurred" class="flex gap-2">
                      <a
                        v-if="adventurer.profile_details?.linkedin_url"
                        :href="adventurer.profile_details.linkedin_url"
                        target="_blank"
                        class="text-gray-400 hover:text-[#0077b5] transition-colors"
                        @click.stop
                      >
                        <img
                          src="/linkedin_logo.svg"
                          class="w-3.5 h-3.5 opacity-60 hover:opacity-100 transition-opacity"
                          alt="LinkedIn"
                        />
                      </a>
                      <a
                        v-if="adventurer.profile_details?.telegram_handle"
                        :href="`https://t.me/${adventurer.profile_details.telegram_handle}`"
                        target="_blank"
                        class="text-gray-400 hover:text-[#0088cc] transition-colors"
                        @click.stop
                      >
                        <img
                          src="/telegram_logo.svg"
                          class="w-3.5 h-3.5 opacity-60 hover:opacity-100 transition-opacity"
                          alt="Telegram"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </td>

            <!-- Stage Status Columns -->
            <td
              v-for="stage in stages"
              :key="stage"
              class="py-4 px-4 min-w-[130px] relative"
            >
              <div @click.stop class="inline-block">
                <Popover>
                  <PopoverTrigger as-child>
                    <StatusBadge
                    :status="adventurer[`${stage}_status`] || ''"
                    :has-notes="!!adventurer[`${stage}_status_notes`]"
                  />
                </PopoverTrigger>
                <PopoverContent class="w-72 p-4">
                  <StatusQuickEditPopover
                    :title="`${getStageTitle(stage)} Stage`"
                    :attempts="adventurer[`${stage}_status_attempts`] || 0"
                    :notes="adventurer[`${stage}_status_notes`] || ''"
                    :is-saving="isUpdatingStatus[`${adventurer.id}_${stage}`]"
                    @save="(data) => handlePopoverSave(data, stage, adventurer)"
                  >
                    <template #status-select>
                      <CodingStatusSelect
                        v-if="stage === 'coding'"
                        :model-value="adventurer[`${stage}_status`] || ''"
                        @update:model-value="(val) => handlePopoverStatusChange(val, stage, adventurer)"
                        :disabled="isUpdatingStatus[`${adventurer.id}_${stage}`]"
                      />
                      <SystemDesignStatusSelect
                        v-else-if="stage === 'system_design'"
                        :model-value="adventurer[`${stage}_status`] || ''"
                        @update:model-value="(val) => handlePopoverStatusChange(val, stage, adventurer)"
                        :disabled="isUpdatingStatus[`${adventurer.id}_${stage}`]"
                      />
                      <StageStatusSelect
                        v-else
                        :model-value="adventurer[`${stage}_status`] || ''"
                        @update:model-value="(val) => handlePopoverStatusChange(val, stage, adventurer)"
                        :disabled="isUpdatingStatus[`${adventurer.id}_${stage}`]"
                      />
                    </template>
                  </StatusQuickEditPopover>
                </PopoverContent>
                </Popover>
              </div>
            </td>

            <!-- Expand Toggle Column -->
            <td class="py-4 px-4 w-16 min-w-[64px] text-center">
              <div
                class="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-all inline-flex"
              >
                <ChevronDown 
                  class="w-4 h-4 transition-transform duration-200" 
                  :class="{ 'rotate-180': expandedRowId === adventurer.id }"
                />
              </div>
            </td>
          </tr>

          <!-- Expanded Row -->
          <BatchProgressRowExpanded
            :adventurer="adventurer"
            :is-expanded="expandedRowId === adventurer.id"
            :is-updating-status="isUpdatingStatus"
            :resume-reset-trigger="resumeResetTrigger"
            @stage-update="(stage, changes) => handleStageUpdate(stage, changes, adventurer)"
          />
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from 'lucide-vue-next'
import FellowAvatarDropdown from '@/components/common/FellowAvatarDropdown.vue'
import { useToast } from '@/components/ui/toast/use-toast'
import BatchProgressRowExpanded from './BatchProgressRowExpanded.vue'
import StatusBadge from './StatusBadge.vue'
import StatusQuickEditPopover from './StatusQuickEditPopover.vue'
import StageStatusSelect from './StageStatusSelect.vue'
import CodingStatusSelect from './CodingStatusSelect.vue'
import SystemDesignStatusSelect from './SystemDesignStatusSelect.vue'
import ResumeUploadModal from './ResumeUploadModal.vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { Adventurer, Stage } from '@/types/adventurer'
// @ts-ignore
import { useProgressStatusUpdate } from '@/composables/useProgressStatusUpdate'
import { getStageTitle, PROGRESS_STATUS } from '@/const/progressStatus'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const props = defineProps<{
  batchName: string
  adventurers: Adventurer[]
  clickableHeader?: boolean
}>()

defineEmits<{
  (e: 'header-click'): void
}>()

// Create a local reactive copy of adventurers
const localAdventurers = ref<Adventurer[]>([])
const expandedRowId = ref<number | null>(null)
const isUpdatingStatus = ref<Record<string, boolean>>({})

const { toast } = useToast()
const nameSortDirection = ref<'asc' | 'desc' | null>(null)
const { isBlurred } = usePrivacyMode()

// Resume upload modal state
const resumeUploadModal = ref<{
  show: boolean
  adventurer: Adventurer | null
  pendingChanges: { status?: string; attempts?: number; notes?: string } | null
}>({ show: false, adventurer: null, pendingChanges: null })

// Incremented when the upload modal is cancelled, so the expanded row can revert
const resumeResetTrigger = ref(0)

// Available stages
const stages: Stage[] = ['resume', 'behavioral', 'coding', 'system_design']

// Use the composable for API calls
const { updateProgressStatus } = useProgressStatusUpdate()

const sortedAdventurers = computed(() => {
  if (!nameSortDirection.value) return localAdventurers.value

  return [...localAdventurers.value].sort((a, b) => {
    const nameA =
      `${a.profile_details?.user_details?.first_name} ${a.profile_details?.user_details?.last_name}`.toLowerCase()
    const nameB =
      `${b.profile_details?.user_details?.first_name} ${b.profile_details?.user_details?.last_name}`.toLowerCase()

    if (nameSortDirection.value === 'asc') {
      return nameA.localeCompare(nameB)
    } else {
      return nameB.localeCompare(nameA)
    }
  })
})

const toggleNameSort = () => {
  if (!nameSortDirection.value) {
    nameSortDirection.value = 'asc'
  } else if (nameSortDirection.value === 'asc') {
    nameSortDirection.value = 'desc'
  } else {
    nameSortDirection.value = null
  }
}

const toggleRowExpansion = (adventurerId: number) => {
  if (expandedRowId.value === adventurerId) {
    expandedRowId.value = null
  } else {
    expandedRowId.value = adventurerId
  }
}

const handlePopoverStatusChange = async (value: string, stage: Stage, adventurer: Adventurer) => {
  await handleStageUpdate(stage, { status: value }, adventurer)
}

const onResumeUploadSuccess = async () => {
  const { adventurer, pendingChanges } = resumeUploadModal.value
  resumeUploadModal.value = { show: false, adventurer: null, pendingChanges: null }
  if (adventurer && pendingChanges) {
    // Call the raw executor directly — skip the intercept so we don't re-open the modal
    await executeStageUpdate('resume', pendingChanges, adventurer)
  }
}

const onResumeUploadCancel = () => {
  resumeUploadModal.value = { show: false, adventurer: null, pendingChanges: null }
  // Tell any open expanded row to revert the resume status dropdown
  resumeResetTrigger.value++
}

const handlePopoverSave = async (data: { attempts: number; notes: string }, stage: Stage, adventurer: Adventurer) => {
  await handleStageUpdate(stage, data, adventurer)
}

/**
 * Gate: intercepts resume→PASSED to require a resume upload first, then delegates to executeStageUpdate.
 * Both the inline popover and the expanded row's Save button funnel through here.
 */
const handleStageUpdate = async (
  stage: Stage,
  changes: { status?: string; attempts?: number; notes?: string },
  adventurer: Adventurer
) => {
  if (stage === 'resume' && changes.status === PROGRESS_STATUS.PASSED) {
    resumeUploadModal.value = { show: true, adventurer, pendingChanges: changes }
    return
  }
  await executeStageUpdate(stage, changes, adventurer)
}

/** Raw API update + local state sync. Never re-opens the modal. */
const executeStageUpdate = async (
  stage: Stage,
  changes: { status?: string; attempts?: number; notes?: string },
  adventurer: Adventurer
) => {
  if (!adventurer.profile_details?.user_details?.username) return

  const updateKey = `${adventurer.id}_${stage}`
  if (isUpdatingStatus.value[updateKey]) return

  isUpdatingStatus.value[updateKey] = true

  try {
    await updateProgressStatus(adventurer.profile_details.user_details.username, stage, changes)

    // Update local state
    if (changes.status) {
      const statusField = `${stage}_status` as keyof Adventurer
      ;(adventurer as any)[statusField] = changes.status
    }
    if (changes.attempts !== undefined) {
      const attemptsField = `${stage}_status_attempts` as keyof Adventurer
      ;(adventurer as any)[attemptsField] = changes.attempts
    }
    if (changes.notes !== undefined) {
      const notesField = `${stage}_status_notes` as keyof Adventurer
      ;(adventurer as any)[notesField] = changes.notes
    }

    // Sync back into the local array so the table re-renders
    const index = localAdventurers.value.findIndex((a) => a.id === adventurer.id)
    if (index !== -1) {
      localAdventurers.value[index] = { ...adventurer }
    }

    toast({ title: `${getStageTitle(stage)} updated`, description: changes.status ? `Status set to ${changes.status.replace(/_/g, ' ').toLowerCase()}` : 'Changes saved.' })
  } catch (error) {
    console.error(`Error updating ${stage}:`, error)
    toast({ title: `Failed to update ${getStageTitle(stage)}`, description: 'Please try again.', variant: 'destructive' })
  } finally {
    isUpdatingStatus.value[updateKey] = false
  }
}

// Initialize local adventurers
onMounted(() => {
  localAdventurers.value = props.adventurers.map((adventurer) => ({
    ...adventurer,
    batch: adventurer.batch || props.batchName
  }))
})

// Watch for changes in props.adventurers
watch(
  () => props.adventurers,
  (newAdventurers) => {
    localAdventurers.value = newAdventurers.map((adventurer) => ({
      ...adventurer,
      batch: adventurer.batch || props.batchName
    }))
  },
  { deep: true }
)
</script>
